import { useEffect, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { api } from "@/lib/api";

const cache = {};
export const clearOptionsCache = () => Object.keys(cache).forEach((k) => delete cache[k]);

export function useOptions(field) {
  const isRef = field.type === "refitem" || field.type === "multirefitem";
  const key = isRef ? `refs:${field.ref}` : field.ref;
  const [opts, setOpts] = useState(cache[key] || []);
  useEffect(() => {
    if (!field.ref || cache[key]) return;
    const url = isRef ? `/options/refs?type=${field.ref}` : `/options/${field.ref}`;
    api.get(url).then((r) => {
      cache[key] = r.data;
      setOpts(r.data);
    }).catch(() => {});
  }, [key, field.ref, isRef]);
  return opts;
}

export function MultiRefSelect({ field, value, onChange, testId }) {
  const opts = useOptions(field);
  const sel = value || [];
  const [open, setOpen] = useState(false);
  const toggle = (id) => onChange(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  const labels = opts.filter((o) => sel.includes(o.id)).map((o) => o.label);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className="w-full min-h-9 px-3 py-1.5 border rounded-md bg-background text-sm flex items-center justify-between gap-2 hover:border-foreground/30 transition-colors text-left">
          <span className={`truncate ${labels.length ? "" : "text-muted-foreground"}`}>{labels.length ? labels.join(", ") : "Sélectionner…"}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[220px]" align="start">
        <Command>
          <CommandInput placeholder="Rechercher…" />
          <CommandList>
            <CommandEmpty>Aucun résultat</CommandEmpty>
            {opts.map((o) => (
              <CommandItem key={o.id} value={`${o.label} ${o.id}`} onSelect={() => toggle(o.id)}>
                <Check className={`h-3.5 w-3.5 mr-2 ${sel.includes(o.id) ? "opacity-100" : "opacity-0"}`} />
                {o.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function RefSelect({ field, value, onChange, filterFn, testId, allowAll, placeholder }) {
  const all = useOptions(field);
  const opts = filterFn ? all.filter(filterFn) : all;
  const [open, setOpen] = useState(false);
  const current = all.find((o) => o.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className="w-full h-9 px-3 border rounded-md bg-background text-sm flex items-center justify-between gap-2 hover:border-foreground/30 transition-colors">
          <span className={`truncate ${current ? "" : "text-muted-foreground"}`}>
            {current ? current.label : placeholder || (allowAll ? "Tous" : "Sélectionner…")}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[220px]" align="start">
        <Command>
          <CommandInput placeholder="Rechercher…" />
          <CommandList>
            <CommandEmpty>Aucun résultat</CommandEmpty>
            <CommandItem value="__none__" onSelect={() => { onChange(null); setOpen(false); }}>
              <span className="text-muted-foreground">{allowAll ? "Tous" : "— Aucun —"}</span>
            </CommandItem>
            {opts.map((o) => (
              <CommandItem key={o.id} value={`${o.label} ${o.id}`} onSelect={() => { onChange(o.id); setOpen(false); }}>
                <Check className={`h-3.5 w-3.5 mr-2 ${o.id === value ? "opacity-100" : "opacity-0"}`} />
                {o.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function OptionSelect({ options, value, onChange, testId, allowAll }) {
  return (
    <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? null : v)}>
      <SelectTrigger data-testid={testId} className="h-9">
        <SelectValue placeholder="Sélectionner…" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__all">{allowAll ? "Tous" : "— Sélectionner —"}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function FieldInput({ field, value, onChange, form, testId }) {
  const t = field.type;
  if (t === "select") return <OptionSelect options={field.options} value={value} onChange={onChange} testId={testId} />;
  if (t === "multirefitem") return <MultiRefSelect field={field} value={value} onChange={onChange} testId={testId} />;
  if (t === "ref" || t === "refitem") {
    let filterFn = null;
    if (field.name === "tpe_id" && form?.revendeur_id) filterFn = (o) => o.revendeur_id === form.revendeur_id;
    if (field.name === "superviseur_id" && form?.zone_id) filterFn = (o) => (o.zone_ids || []).includes(form.zone_id);
    if (field.filter_by && form?.[field.filter_by]) filterFn = (o) => o[field.filter_by] === form[field.filter_by];
    return <RefSelect field={field} value={value} onChange={onChange} filterFn={filterFn} testId={testId} />;
  }
  if (t === "textarea") return <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} rows={3} />;
  if (t === "bool") return <Switch checked={!!value} onCheckedChange={onChange} data-testid={testId} />;
  return (
    <Input
      type={t === "number" ? "number" : t === "date" ? "date" : "text"}
      step="any"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className={t === "number" ? "font-mono" : ""}
    />
  );
}
